import React, { useEffect, useState, type ReactElement } from "react";
import type { RdScheduleActionScope } from "../../../../../shared/rd-schedule-actions";
import {
  localParts,
  moveConfirmLabel,
  timeLabel,
  weekdayName,
  whenLabel,
} from "../../../lib/rd-schedule-actions";
import type { ActionAppointment, ActionDialogContext } from "./action-types";
import { ActionDialogFrame, ActionForm, OptionsStatus } from "./action-frame";
import { EmailCheckbox, ScopeFields, TimePicker } from "./action-parts";
import { useActionOptions, type ActionOptions } from "./use-action-options";
import { useActionSubmit, useScheduleApi } from "./use-action-submit";
import { useTimePick, type TimePick } from "./use-time-pick";

// NO-876. Reschedule (spec § Appointment actions; canvas Reschedule.dc.html):
// a new date, the open times for it, the escape hatch for a time outside the
// RD's hours, which visits move, and an email that is on by default. The
// confirm button names the new time.

export interface RescheduleDialogProps extends ActionDialogContext {
  appointment: ActionAppointment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void;
}

export function RescheduleDialog(props: RescheduleDialogProps): ReactElement {
  const { appointment, timeZone, open, onOpenChange } = props;
  const [pending, setPending] = useState(false);

  return (
    <ActionDialogFrame
      open={open}
      onOpenChange={onOpenChange}
      pending={pending}
      title="Reschedule appointment"
      subtitle={`${appointment.clientName}${appointment.typeName ? `, ${appointment.typeName}` : ""}. Now ${whenLabel(appointment.startsAt, timeZone)}.`}
    >
      <RescheduleBody {...props} onPending={setPending} />
    </ActionDialogFrame>
  );
}

function RescheduleBody(
  props: RescheduleDialogProps & { onPending: (pending: boolean) => void }
): ReactElement {
  const api = useScheduleApi();
  const { appointment, rdId } = props;
  const { state, reload } = useActionOptions(api, rdId, appointment.id);
  const close = () => props.onOpenChange(false);

  if (state.status !== "ready") {
    return <OptionsStatus state={state} onRetry={reload} onClose={close} />;
  }

  return <RescheduleForm {...props} options={state.options} onClose={close} />;
}

export interface RescheduleFormModel {
  date: string;
  setDate: (date: string) => void;
  scope: RdScheduleActionScope;
  setScope: (scope: RdScheduleActionScope) => void;
  email: boolean;
  setEmail: (email: boolean) => void;
}

function useRescheduleModel(
  appointment: ActionAppointment,
  timeZone: string
): RescheduleFormModel {
  const [date, setDate] = useState(localParts(appointment.startsAt, timeZone).date);
  const [scope, setScope] = useState<RdScheduleActionScope>("this");
  const [email, setEmail] = useState(true);

  return { date, setDate, scope, setScope, email, setEmail };
}

function moveLabel(date: string, pick: Pick<TimePick, "localTime">): string {
  if (!date || !pick.localTime) return "Move appointment";

  return moveConfirmLabel(date, pick.localTime);
}

function RescheduleForm(
  props: RescheduleDialogProps & {
    options: ActionOptions;
    onClose: () => void;
    onPending: (pending: boolean) => void;
  }
): ReactElement {
  const { appointment, rdId, timeZone, options, onClose, onChanged, onPending } = props;
  const api = useScheduleApi();
  const model = useRescheduleModel(appointment, timeZone);
  const pick = useTimePick({
    api,
    rdId,
    date: model.date,
    appointmentTypeId: options.appointment.appointmentTypeId,
    excludeAppointmentId: appointment.id,
    fallbackTimeZone: timeZone,
  });
  const action = useActionSubmit({
    api,
    rdId,
    timeZone,
    onPartial: onChanged,
    onSucceeded: () => {
      onChanged();
      onClose();
    },
    onSlotConflict: pick.reload,
  });
  const ready = pick.startsAt !== null && pick.localTime !== null;

  useEffect(() => onPending(action.pending), [onPending, action.pending]);

  const submit = () => {
    if (!pick.startsAt) return;

    void action.submit({
      kind: "reschedule",
      appointmentId: appointment.id,
      startsAt: pick.startsAt,
      outsideHours: pick.outside,
      scope: model.scope,
      emailClient: model.email,
    });
  };

  return (
    <ActionForm
      onSubmit={submit}
      problem={action.problem}
      back={{ label: "Back", onClick: onClose }}
      confirm={{
        label: moveLabel(model.date, pick),
        ...action.confirmState,
        disabled: !ready,
      }}
    >
      <RescheduleFields
        {...{ appointment, options, model, pick, timeZone }}
        disabled={action.pending || action.locked}
      />
    </ActionForm>
  );
}

function scopeHints(
  options: ActionOptions,
  appointment: ActionAppointment,
  timeZone: string,
  date: string
): { thisHint: string; laterHint: string } | null {
  const series = options.series;

  if (!series) return null;

  const original = localParts(appointment.startsAt, timeZone);

  return {
    thisHint: `Later ${weekdayName(original.date)} visits stay at ${timeLabel(original.time)}`,
    laterHint: `The recurring time moves to ${weekdayName(date)}s`,
  };
}

export function RescheduleFields(props: {
  appointment: ActionAppointment;
  options: ActionOptions;
  model: RescheduleFormModel;
  pick: TimePick;
  timeZone: string;
  disabled: boolean;
}): ReactElement {
  const { appointment, options, model, pick, timeZone, disabled } = props;
  const hints = scopeHints(options, appointment, timeZone, model.date);

  return (
    <>
      <TimePicker
        idPrefix="reschedule"
        dateLabel="New date"
        date={model.date}
        onDateChange={model.setDate}
        pick={pick}
        durationMinutes={options.appointment.durationMinutes}
        disabled={disabled}
      />
      {hints ? (
        <ScopeFields
          idPrefix="reschedule-scope"
          legend="Which visits move"
          value={model.scope}
          onChange={model.setScope}
          disabled={disabled}
          thisLabel="Only this visit"
          thisHint={hints.thisHint}
          laterLabel="This and all later visits"
          laterHint={hints.laterHint}
          laterDisabledNote={
            options.series?.tooLong
              ? "This series has too many later visits to move at once."
              : undefined
          }
        />
      ) : null}
      <EmailCheckbox
        id="reschedule-email"
        label={`Email ${appointment.firstName} the new time`}
        checked={model.email}
        disabled={disabled}
        onChange={model.setEmail}
      />
    </>
  );
}
